/**
 * ParamPanel — 3D parameter panel driven by a product's JSON schema.
 *
 * The schema is generated from the backend's FieldSpec definitions
 * (`manage.py dump_product_schema`), so ranges, steps, units and help text come
 * from the same place that clamps them at build time. Adding a parameter to a
 * product means editing one Python tuple and regenerating the JSON — no JSX.
 *
 * Values are read from and written to `form2d_data` at each field's `path`,
 * which is the historical spelling the rest of the app already uses
 * (`baseLayout.rounded`, `wallThicknessMm`), never the internal snake_case key.
 */
import React from 'react';
import { Checkbox, InputSpinner, RadioGroup, SelectDropdown } from '../../../../../../components/ui';
import { readPath } from '../../../../../../products/registry';
import ColorPicker from '../ColorPicker.jsx';

const roundTo = (value, decimals) => {
    const factor = 10 ** (Number.isFinite(decimals) ? decimals : 2);
    return Math.round(Number(value) * factor) / factor;
};

const FieldRow = ({ field, values, onChange }) => {
    const raw = readPath(values, field.path);
    const value = raw === undefined ? field.default : raw;

    const help = field.help ? (
        <div className="mt-1 rounded-lg border border-info/30 bg-info/10 px-2 py-1 text-[11px] leading-snug text-base-content/70">
            {field.help}
        </div>
    ) : null;

    if (field.kind === 'boolean') {
        return (
            <div className="flex flex-col">
                <Checkbox
                    checked={Boolean(value)}
                    onChange={(next) => onChange(field.path, Boolean(next))}
                    label={field.label}
                />
                {help}
            </div>
        );
    }

    if (field.kind === 'enum') {
        const options = field.options || [];
        const Control = options.length > 4 ? SelectDropdown : RadioGroup;
        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-base-content/80">{field.label}</span>
                <Control
                    value={value}
                    onChange={(next) => onChange(field.path, next?.value ?? next)}
                    options={options}
                    direction="vertical"
                />
                {help}
            </div>
        );
    }

    if (field.kind === 'color') {
        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-base-content/80">{field.label}</span>
                <ColorPicker
                    value={value || '#ffffff'}
                    onChange={(next) => onChange(field.path, next)}
                    allowVaciado={false}
                />
                {help}
            </div>
        );
    }

    if (field.kind === 'frac2') {
        const pair = value || { xFrac: 0.5, yFrac: 0.5 };
        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-base-content/80">{field.label}</span>
                <div className="flex gap-2">
                    {['xFrac', 'yFrac'].map((axis) => (
                        <InputSpinner
                            key={axis}
                            label={axis === 'xFrac' ? 'X' : 'Y'}
                            value={Number(pair[axis] ?? 0.5)}
                            min={0}
                            max={1}
                            initialStep={0.05}
                            onChange={(next) =>
                                onChange(field.path, { ...pair, [axis]: roundTo(next, 2) })
                            }
                        />
                    ))}
                </div>
                {help}
            </div>
        );
    }

    // number
    return (
        <div className="flex flex-col gap-1">
            <InputSpinner
                label={field.unit ? `${field.label} (${field.unit})` : field.label}
                value={Number(value ?? 0)}
                min={field.min ?? 0}
                max={field.max ?? Infinity}
                initialStep={field.step ?? 0.1}
                decimals={field.decimals}
                onChange={(next) => onChange(field.path, roundTo(next, field.decimals))}
            />
            {help}
        </div>
    );
};

/**
 * Collapsible group.
 *
 * Deliberately NOT DaisyUI's `<input type="checkbox">` collapse: that input is
 * stretched over the whole component to catch the toggle click, so it sits on
 * top of everything inside `collapse-content` and swallows the first click on
 * any nested control. It goes unnoticed with plain buttons, but it makes
 * spinners, selects and colour pickers unusable. Controlled state plus
 * `collapse-open`/`collapse-close` has no such overlay.
 */
const Group = ({ group, values, onChange, defaultOpen, action }) => {
    const [open, setOpen] = React.useState(Boolean(defaultOpen));

    return (
        <div
            className={`collapse collapse-arrow rounded-xl bg-base-200/60 ${
                open ? 'collapse-open' : 'collapse-close'
            }`}
        >
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
                className="collapse-title min-h-0 w-full py-2 text-left text-sm font-semibold cursor-pointer"
            >
                {group.label}
            </button>
            <div className="collapse-content">
                <div className="flex flex-col gap-3 pb-2">
                    {(group.fields || []).map((field) => (
                        <FieldRow
                            key={field.key}
                            field={field}
                            values={values}
                            onChange={onChange}
                        />
                    ))}
                    {action}
                </div>
            </div>
        </div>
    );
};

/**
 * @param product      entry from products/registry
 * @param values       the current form2d_data
 * @param onChange     (path, value) => void
 * @param groupActions map of group key -> extra node rendered inside that group
 *                     (e.g. "place the hanging-slot marker on the canvas")
 * @param footer       rendered under the groups (diagnostics panel, etc.)
 */
const ParamPanel = ({ product, values, onChange, groupActions = {}, footer = null }) => {
    const hidden = product?.hiddenSchemaFields || [];
    const groups = (product?.paramSchema?.groups || [])
        // A field can be owned by a dedicated panel instead (see hiddenSchemaFields).
        .map((group) => ({
            ...group,
            fields: (group.fields || []).filter((field) => !hidden.includes(field.key)),
        }))
        .filter((group) => group.fields.length || groupActions[group.key]);
    if (!groups.length) return null;

    return (
        <div className="flex flex-col gap-2 p-1">
            {groups.map((group, index) => (
                <Group
                    key={group.key}
                    group={group}
                    values={values}
                    onChange={onChange}
                    action={groupActions[group.key]}
                    // Open the first two groups: dimensions and the box itself
                    // are what you reach for; the rest are fine-tuning.
                    defaultOpen={index < 2}
                />
            ))}
            {footer}
        </div>
    );
};

export default ParamPanel;
